import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Badge,
  Box,
  Flex,
  useDisclosure,
  useBreakpointValue,
  IconButton,
  Center,
  Text,
} from '@chakra-ui/react';
import ConversationSidebar from '@/features/conversations/components/conversation-sidebar';
import ConversationHeader from '@/features/conversations/components/conversation-header';
import ConversationContainer from '@/features/conversations/components/conversation-container';
import { NavigationTabs } from '@/features/conversations/components/conversation-navigation-tabs';
import DashboardPage from '@/features/dashboard/components/dashboard-page';
import ChannelsPage from '@/pages/channels/channels-page';
import { useConversationsContext } from '@/context/conversation-context';
import { FiMenu } from 'react-icons/fi';
import { useUsers } from '@/hooks/useUsers';

import { useWhatsAppEvents } from '@/context/whatsapp-events-context';
import { useGetConversations } from '@/features/conversations/conversations.hook';

import { useOrgLocation } from '@/context/organization-location-context';
import { CreateWhatsappConversationProvider } from '@/features/conversations/components/useCreateWhatsappConversation';
import { useQuery } from '@tanstack/react-query';
import { useWidgetUserRole } from '@/widgets/hooks/use-widget-user-role';
import { useWhatsAppChannel } from '@/hooks/WAHA/useWhatsapp';
import { useCreateConversation } from '@/features/conversations/conversations.hook';
import { toaster } from '@/components/ui/toaster';

interface ConversationsPageProps {
  hideConversationList?: boolean;
  hideHeader?: boolean;
  userPhone?: string;
  userFirstName?: string;
  userLastName?: string;
  userMember?: boolean;
}

function ConversationsPage(props: ConversationsPageProps) {
  // Read URL parameters directly in the component
  const [hideConversationList] = useState(
    !!props.userPhone || props.hideConversationList || false,
  );

  const [hideHeader] = useState(!!props.hideHeader);

  const [activeTab, setActiveTab] = useState('conversations');
  const { open: isOpen, onOpen, onClose } = useDisclosure();

  // Use responsive helpers for mobile/desktop view
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;

  // Get conversations data from our context instead of the hook
  const {
    selectedConversationId: hookSelectedConversationId,
    setSelectedConversationId: setHookSelectedConversationId,
    refreshConversations,
    loading: isConversationLoading,
    selectedConversation,
  } = useConversationsContext();

  // Convert null to undefined for compatibility with ConversationSidebar
  const compatibleSelectedId = hookSelectedConversationId || undefined;

  // Use our centralized users hook
  const { currentUser } = useUsers();

  const { selectedLocation, selectedOrganisation } = useOrgLocation();
  const { setSelectedConversationIdForContext } = useWhatsAppEvents();
  const { getUserByPhone, createUser } = useUsers();
  const {
    isLoading: isUserByPhoneLoading,
    data: user,
    refetch: refetchUserByPhone,
  } = useQuery({
    queryKey: ['user', 'phone', props.userPhone],
    queryFn: () => getUserByPhone(props.userPhone || ''),
    enabled: !!props.userPhone,
  });

  // Get WhatsApp channels for conversation creation
  const { useWhatsAppChannels } = useWhatsAppChannel();
  const { data: channelsData = [], isLoading: isLoadingChannels } = useWhatsAppChannels();
  const channels = channelsData?.filter((channel) => channel.status === 'WORKING') || [];
  const { mutateAsync: createConversation } = useCreateConversation();
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);

  // Normalize phone number to match API normalization (removes all + and spaces)
  // This matches the API's normalization: str(value).replace('+', '').replace(' ', '').strip()
  const normalizePhoneForAPI = (phone: string): string => {
    return phone.replace(/\+/g, '').replace(/\s/g, '').trim();
  };

  // Debug logging for all values
  console.log('========================================');
  console.log('[ConversationsPage] DEBUG - All Values:');
  console.log('  props.userPhone:', props.userPhone);
  console.log('  props.userFirstName:', props.userFirstName);
  console.log('  props.userLastName:', props.userLastName);
  console.log('  props.userMember:', props.userMember);
  console.log('  props.hideConversationList:', props.hideConversationList);
  console.log('  props.hideHeader:', props.hideHeader);
  console.log('  selectedOrganisation:', selectedOrganisation);
  console.log('  selectedLocation:', selectedLocation);
  console.log('  user (from phone lookup):', user);
  console.log('  isUserByPhoneLoading:', isUserByPhoneLoading);
  console.log('  currentUser:', currentUser);
  console.log('========================================');

  // Filter conversations directly by phone_number using the API
  // The API now supports phone_number filtering which matches both user phone and channel phone
  const { data: conversations, isLoading: isConversationsLoading } =
    useGetConversations({
      page: 1,
      perPage: 1, // Only need the most recent one, sorted by updated_at DESC
      filter: {
        ...(props.userPhone
          ? { phone_number: normalizePhoneForAPI(props.userPhone) } // Filter by phone number via API (normalized to match API)
          : { user_id: user?.id || '' }), // Normal user_id filter when not searching by phone
        organisation_id: selectedOrganisation?.id || '',
        location_id: selectedLocation?.id || '',
      },
      enabled: props.userPhone ? true : !!user,
    });

  useEffect(() => {
    if (conversations && conversations.data && conversations.data.length > 0) {
      // When searching by phone number, API filters by phone_number (matches user or channel phone)
      // So the first (most recent) conversation is the correct one
      // When not searching by phone, use user_id match
      let selectedConversation = null;
      
      if (props.userPhone) {
        // Already filtered by phone_number via API, so first one is correct
        selectedConversation = conversations.data[0];
      } else if (user) {
        // Try to find conversation matching the user_id
        selectedConversation = conversations.data.find(
          (conversation) => conversation.user_id === user.id,
        ) || conversations.data[0]; // Fallback to first if no match
      }
      
      if (selectedConversation) {
        setHookSelectedConversationId(selectedConversation.id);
        setActiveTab('conversations');
      }
    }
  }, [conversations, user, props.userPhone]);

  // Auto-create conversation when userPhone is provided but no conversation exists
  useEffect(() => {
    // Debug logging
    console.log('[AutoCreate] Checking conditions:', {
      userPhone: props.userPhone,
      isConversationsLoading,
      isUserByPhoneLoading,
      isLoadingChannels,
      isCreatingConversation,
      conversationsLength: conversations?.data?.length,
      channelsLength: channels.length,
      userFirstName: props.userFirstName,
      userLastName: props.userLastName,
      selectedOrganisationId: selectedOrganisation?.id,
      selectedLocationId: selectedLocation?.id,
    });

    const shouldCreateConversation =
      props.userPhone &&
      !isConversationsLoading &&
      !isUserByPhoneLoading &&
      !isLoadingChannels &&
      !isCreatingConversation &&
      conversations &&
      conversations.data &&
      conversations.data.length === 0 &&
      channels.length > 0 &&
      props.userFirstName &&
      props.userLastName &&
      selectedOrganisation?.id &&
      selectedLocation?.id;

    console.log('[AutoCreate] shouldCreateConversation:', shouldCreateConversation);

    if (shouldCreateConversation) {
      const createConversationAutomatically = async () => {
        try {
          setIsCreatingConversation(true);
          
          // Format phone number - remove all non-digits (consistent with API normalization)
          // This ensures the phone number stored matches what the API searches for
          const formatPhoneForWhatsApp = (phone: string): string => {
            return phone.replace(/\D/g, '');
          };
          
          const formattedPhone = formatPhoneForWhatsApp(props.userPhone || '');
          
          console.log('[AutoCreate] Phone normalization:', {
            original: props.userPhone,
            formatted: formattedPhone,
            normalizedForAPI: normalizePhoneForAPI(props.userPhone || ''),
          });
          
          // Get or create user
          let userId: string | undefined;
          let existingUser = await getUserByPhone(formattedPhone);
          
          if (existingUser) {
            userId = existingUser.id;
          } else {
            // Create new user
            const newUser = await createUser({
              first_name: props.userFirstName || '',
              last_name: props.userLastName || '',
              phone_number: formattedPhone,
              email: `${formattedPhone}@placeholder.com`,
              organisation_id: selectedOrganisation?.id || '',
              location_id: selectedLocation?.id || '',
              ...(props.userMember !== undefined && { isMember: props.userMember }),
            });
            userId = newUser.id;
          }

          // Create conversation
          const chatId = `${formattedPhone}@c.us`;
          const conversationData = {
            channel_id: channels[0].id,
            phone_number: formattedPhone,
            summary: `WhatsApp Chat (${formattedPhone})`,
            has_agent_active: false,
            wab_chatId_external: chatId,
            ...(userId && { user_id: userId }),
          };

          console.log('[AutoCreate] Creating conversation with data:', conversationData);
          const newConversation = await createConversation(conversationData);
          console.log('[AutoCreate] Conversation creation response:', newConversation);
          
          // The response from createConversationChannel contains the conversation data
          // Check for conversation_id or id in the response
          const conversationId = newConversation?.data?.conversation_id || 
                                 newConversation?.data?.id ||
                                 newConversation?.conversation_id ||
                                 newConversation?.id;
          
          if (conversationId) {
            console.log('[AutoCreate] Conversation created successfully with ID:', conversationId);
            setHookSelectedConversationId(conversationId);
            setSelectedConversationIdForContext(conversationId);
            refreshConversations();
            refetchUserByPhone();
          } else {
            console.error('[AutoCreate] No conversation ID found in response:', newConversation);
            toaster.create({
              title: 'Warning',
              description: 'Conversation was created but ID not found in response',
              type: 'warning',
              duration: 5000,
            });
          }
        } catch (error) {
          console.error('Error auto-creating conversation:', error);
          toaster.create({
            title: 'Error',
            description: 'Failed to create conversation automatically. Please try again.',
            type: 'error',
            duration: 5000,
          });
        } finally {
          setIsCreatingConversation(false);
        }
      };

      createConversationAutomatically();
    }
  }, [
    props.userPhone,
    props.userFirstName,
    props.userLastName,
    isConversationsLoading,
    isUserByPhoneLoading,
    isLoadingChannels,
    isCreatingConversation,
    conversations,
    channels,
    selectedOrganisation?.id,
    selectedLocation?.id,
    getUserByPhone,
    createUser,
    createConversation,
    setHookSelectedConversationId,
    setSelectedConversationIdForContext,
    refreshConversations,
    refetchUserByPhone,
  ]);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
  }, []);

  // Handle conversation selection
  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      if (activeTab !== 'conversations') {
        handleTabChange('conversations');
      }
      setHookSelectedConversationId(conversationId);
      setSelectedConversationIdForContext(conversationId);

      // Close drawer on mobile after selection
      onClose();
    },
    [
      setHookSelectedConversationId,
      setSelectedConversationIdForContext,
      onClose,
      handleTabChange,
      activeTab,
    ],
  );

  // Memoize the sidebar to prevent re-rendering when other things change
  const desktopSidebar = useMemo(
    () =>
      !hideConversationList &&
      !isMobile && (
        <ConversationSidebar
          selectedConversationId={compatibleSelectedId}
          onSelectConversation={handleSelectConversation}
          isMobile={false}
          isDrawerOpen={false}
          onDrawerClose={onClose}
        />
      ),
    [
      compatibleSelectedId,
      handleSelectConversation,
      isMobile,
      hideConversationList,
      onClose,
    ],
  );

  // Memoize the mobile sidebar
  const mobileSidebar = useMemo(
    () =>
      !hideConversationList &&
      isMobile && (
        <ConversationSidebar
          selectedConversationId={compatibleSelectedId}
          onSelectConversation={handleSelectConversation}
          isMobile={true}
          isDrawerOpen={isOpen}
          onDrawerClose={onClose}
        />
      ),
    [
      compatibleSelectedId,
      handleSelectConversation,
      isMobile,
      isOpen,
      onClose,
      hideConversationList,
    ],
  );

  // Add this inside the component
  useEffect(() => {
    // Find the parent custom element
    const findCustomElement = () => {
      let el: HTMLElement | null = mountRef.current;
      while (el && el.tagName !== 'CONVERSATIONS-WIDGET') {
        el = el.parentElement;
      }
      return el;
    };

    const customElement = findCustomElement();

    // Listen for refresh event
    const handleRefresh = () => {
      refreshConversations();
    };

    if (customElement) {
      customElement.addEventListener('refresh-conversations', handleRefresh);
    }

    return () => {
      if (customElement) {
        customElement.removeEventListener(
          'refresh-conversations',
          handleRefresh,
        );
      }
    };
  }, [refreshConversations]);
  // Add a ref to the outer container
  const mountRef = useRef<HTMLDivElement>(null);

  // Get role from widget config (if in widget context) or from current user
  const widgetRole = useWidgetUserRole();
  const roleName = widgetRole || currentUser?.role_name;
  const isAdmin = roleName === 'admin';
  const isManager = roleName === 'manager';
  const isEmployee = roleName === 'employee';
  const formattedRoleName = roleName
    ? roleName.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
    : undefined;

  useEffect(() => {
    if (isAdmin) {
      if (!['conversations', 'dashboard', 'channels'].includes(activeTab)) {
        setActiveTab('conversations');
      }
    } else if (isManager) {
      if (!['conversations', 'dashboard'].includes(activeTab)) {
        setActiveTab('conversations');
      }
    } else if (activeTab !== 'conversations') {
      setActiveTab('conversations');
    }
  }, [isAdmin, isManager, activeTab]);

  const defaultData =
    user || isUserByPhoneLoading
      ? undefined
      : props.userFirstName && props.userLastName && props.userPhone
        ? {
            userFirstName: props.userFirstName || '',
            userLastName: props.userLastName || '',
            userMember: props.userMember || false,
            phoneNumber: props.userPhone || '',
          }
        : undefined;

  return (
    <CreateWhatsappConversationProvider
      defaultData={defaultData}
      refetchUserByPhone={() => refetchUserByPhone()}
    >
      <Flex
        h="100%"
        w="100%"
        overflow="hidden"
        ref={mountRef}
        position="relative"
      >
        {/* Mobile menu button - only shown on mobile */}
        {isMobile && !hideConversationList && (
          <IconButton
            aria-label="Open sidebar"
            position="absolute"
            top={4}
            left={4}
            zIndex={20}
            onClick={onOpen}
            variant="ghost"
          >
            <FiMenu />
          </IconButton>
        )}

        {/* Sidebar on the left - fixed width */}
        {desktopSidebar}

        {/* Main content area with header and content */}
        <Flex
          direction="column"
          flex="1"
          minWidth="0"
          height="100%"
          overflow="hidden"
          borderLeft={props.userPhone ? 'none' : '2px solid'}
          borderLeftColor="gray.100"
          ml={props.userPhone ? 0 : 4}
        >
          {/* Header or embedded navbar */}
          {!hideHeader ? (
            <ConversationHeader
              isMobile={isMobile ?? false}
              onSidebarToggle={onOpen}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              isEmployee={isEmployee}
              isAdmin={isAdmin}
              isManager={isManager}
              roleName={roleName}
            />
          ) : (
            <Box
              bg="white"
              borderBottom="1px"
              borderColor="gray.200"
              px={{ base: 4, md: 6 }}
              py={4}
            >
              <Flex
                align={{ base: 'flex-start', md: 'center' }}
                justify="space-between"
                gap={4}
                flexWrap={{ base: 'wrap', md: 'nowrap' }}
              >
                <Box>
                  {formattedRoleName && (
                    <Badge
                      fontSize="xs"
                      color="gray.600"
                      bg="gray.100"
                      borderRadius="full"
                      px={3}
                      py={0.5}
                      textTransform="capitalize"
                    >
                      {formattedRoleName}
                    </Badge>
                  )}
                </Box>
                <NavigationTabs
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                  isEmployee={isEmployee}
                  isAdmin={isAdmin}
                  isManager={isManager}
                />
              </Flex>
            </Box>
          )}

          {/* Content container - fills remaining space */}
          <Box flex="1" overflow="auto" minHeight="0" position="relative">
            {(activeTab === 'conversations' && isConversationLoading) ||
            isConversationsLoading ||
            isUserByPhoneLoading ||
            isLoadingChannels ||
            isCreatingConversation ? (
              <Center height="100%">
                <Text>
                  {isCreatingConversation
                    ? 'Creating conversation...'
                    : 'Loading...'}
                </Text>
              </Center>
            ) : (
              activeTab === 'conversations' &&
              (hookSelectedConversationId && selectedConversation ? (
                <ConversationContainer
                  conversationId={hookSelectedConversationId}
                  conversation={selectedConversation}
                  refreshConversations={refreshConversations}
                  currentUser={currentUser}
                  isConversationLoading={
                    isConversationLoading ||
                    isConversationsLoading ||
                    isUserByPhoneLoading
                  }
                />
              ) : (
                <Center height="100%">
                  <Text color="gray.500" fontSize="sm">
                    {!hideConversationList
                      ? 'Please select a conversation'
                      : 'No conversation found, if you are creating a new conversation, please wait for it to be created'}
                  </Text>
                </Center>
              ))
            )}

            {activeTab === 'dashboard' && (isAdmin || isManager) && (
              <Box p={6} bg="white" height="100%" overflowY="auto">
                <DashboardPage />
              </Box>
            )}

            {activeTab === 'channels' && isAdmin && (
              <Box bg="white" height="100%" overflowY="auto">
                <ChannelsPage />
              </Box>
            )}
          </Box>
        </Flex>
        {mobileSidebar}
      </Flex>
    </CreateWhatsappConversationProvider>
  );
}

export default ConversationsPage;
